import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Check, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import WeKitLogo from "@/components/WeKitLogo";
import { GOALS, TIMEFRAMES } from "@/data/goals";
import { useQueryClient } from "@tanstack/react-query";

type Step = "goal" | "timeframe";

/**
 * Onboarding is deliberately light: two taps, no typing. Name, city and the
 * rest of the profile live on the profile page, where there's room for them —
 * asking for all of it here made the very first screen feel like paperwork.
 */
const Onboarding = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState<Step>("goal");
  const [goal, setGoal] = useState<string | null>(null);
  const [timeframe, setTimeframe] = useState<string | null>(null);

  // Keyed on the id, not the user object: Supabase hands us a fresh object on
  // every auth event, and re-running this effect used to cancel the in-flight
  // read and leave the page spinning forever.
  const userId = user?.id;
  const firstName = ((user?.user_metadata?.full_name as string) ?? "").trim().split(" ")[0];

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!userId) return;
      try {
        const { data } = await (supabase as any)
          .from("profiles")
          .select("primary_goal, goal_timeframe")
          .eq("id", userId)
          .maybeSingle();
        if (cancelled) return;
        setGoal(data?.primary_goal ?? null);
        setTimeframe(data?.goal_timeframe ?? null);
      } catch {
        // A failed read must never block the form — it's all optional prefill.
      } finally {
        // Never leave the learner staring at a spinner.
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const finish = async (chosenTimeframe: string) => {
    if (!user?.id || !goal) return;
    setSaving(true);

    const base = {
      primary_goal: goal,
      goal_timeframe: chosenTimeframe,
      onboarding_completed_at: new Date().toISOString(),
    };

    // The row almost always exists already (it's created on sign-up), so try the
    // narrowest write first: an update touches no locked columns and needs no
    // insert path. Only if nothing was updated do we create the row.
    let failure: string | null = null;
    try {
      const updated = await (supabase as any)
        .from("profiles")
        .update(base)
        .eq("id", user.id)
        .select("id");

      if (updated.error) failure = updated.error.message ?? "update failed";

      if (!updated.error && (updated.data?.length ?? 0) === 0) {
        const inserted = await (supabase as any)
          .from("profiles")
          .upsert({ id: user.id, email: user.email, ...base }, { onConflict: "id" });
        failure = inserted.error ? (inserted.error.message ?? "insert failed") : null;
      }
    } catch (e) {
      failure = e instanceof Error ? e.message : "network error";
    }

    setSaving(false);
    if (failure) {
      // Log the real reason — the toast stays kind, the console stays useful.
      console.error("[onboarding] could not save goal", failure);
      toast.error("We couldn't save that just now. Try once more.");
      return;
    }
    // Keep the destination the learner originally asked for so the paywall
    // can hand them back to it once access is confirmed.
    const next = new URLSearchParams(window.location.search).get("next");
    await queryClient.invalidateQueries({ queryKey: ["learner-start", user.id] });
    const destination = next ?? (goal === "switch_path" ? "/next-steps#career-switch-roadmap" : null);
    navigate(destination ? `/payment?next=${encodeURIComponent(destination)}` : "/payment", { replace: true });
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" aria-label="Loading" />
      </div>
    );
  }

  const stepIndex = step === "goal" ? 0 : 1;

  return (
    <main className="min-h-screen bg-background px-5 py-10">
      <div className="mx-auto w-full max-w-xl">
        <WeKitLogo size="sm" clickable={false} className="mb-8" />

        {/* Two dots, not a form counter — this should feel like a quest, not a queue. */}
        <div className="flex items-center gap-2" role="progressbar" aria-valuemin={1} aria-valuemax={2} aria-valuenow={stepIndex + 1} aria-label={`Step ${stepIndex + 1} of 2`}>
          {[0, 1].map((i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i <= stepIndex ? "w-10 bg-primary" : "w-6 bg-border"
              }`}
            />
          ))}
          <span className="ml-1 text-xs text-muted-foreground">{stepIndex + 1} of 2</span>
        </div>

        <AnimatePresence mode="wait">
          {step === "goal" ? (
            <motion.section
              key="goal"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
              className="mt-6"
              aria-labelledby="ob-goal-heading"
            >
              <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-primary font-semibold">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" /> Your quest
              </p>
              <h1 id="ob-goal-heading" className="mt-2 text-3xl font-semibold tracking-tight text-foreground">
                {firstName ? `${firstName}, what are you here for?` : "What are you here for?"}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Pick one. We'll shape everything that follows around it — and you can change it any time.
              </p>

              <div className="mt-6 grid gap-3">
                {GOALS.map((g, i) => {
                  const active = goal === g.id;
                  return (
                    <motion.button
                      key={g.id}
                      type="button"
                      aria-pressed={active}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.04 * i }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setGoal(g.id);
                        // One tap moves you forward. No "Next" to hunt for.
                        window.setTimeout(() => setStep("timeframe"), 220);
                      }}
                      className={`flex min-h-[64px] w-full items-center gap-4 rounded-3xl border p-4 text-left transition-colors ${
                        active ? "border-primary bg-primary/10" : "border-border bg-card/60 hover:border-primary/40"
                      }`}
                    >
                      <span className="text-2xl" aria-hidden="true">{g.emoji}</span>
                      <span className="flex-1">
                        <span className="block text-sm font-medium text-foreground">{g.label}</span>
                        <span className="block text-xs text-muted-foreground">{g.hint}</span>
                      </span>
                      <span
                        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                          active ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"
                        }`}
                        aria-hidden="true"
                      >
                        {active && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                      </span>
                    </motion.button>
                  );
                })}
              </div>
            </motion.section>
          ) : (
            <motion.section
              key="timeframe"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
              className="mt-6"
              aria-labelledby="ob-time-heading"
            >
              <p className="text-xs uppercase tracking-[0.16em] text-primary font-semibold">Almost there</p>
              <h1 id="ob-time-heading" className="mt-2 text-3xl font-semibold tracking-tight text-foreground">
                When does this matter?
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                This sets the pace of your plan. Nothing here locks you in.
              </p>

              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {TIMEFRAMES.map((t, i) => {
                  const active = timeframe === t.id;
                  return (
                    <motion.button
                      key={t.id}
                      type="button"
                      aria-pressed={active}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.04 * i }}
                      whileTap={{ scale: 0.98 }}
                      disabled={saving}
                      onClick={() => {
                        setTimeframe(t.id);
                        finish(t.id);
                      }}
                      className={`flex min-h-[64px] items-center gap-3 rounded-3xl border p-4 text-left transition-colors ${
                        active ? "border-primary bg-primary/10" : "border-border bg-card/60 hover:border-primary/40"
                      }`}
                    >
                      <span className="text-2xl" aria-hidden="true">{t.emoji}</span>
                      <span className="text-sm font-medium text-foreground">{t.label}</span>
                    </motion.button>
                  );
                })}
              </div>

              <div className="mt-8 flex items-center justify-between gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  className="min-h-12 rounded-full"
                  onClick={() => setStep("goal")}
                  disabled={saving}
                >
                  <ArrowLeft className="mr-1 h-4 w-4" aria-hidden="true" /> Back
                </Button>
                <Button
                  type="button"
                  className="min-h-12 flex-1 rounded-full sm:flex-none sm:px-8"
                  disabled={!timeframe || saving}
                  onClick={() => timeframe && finish(timeframe)}
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : (
                    <>Continue <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" /></>
                  )}
                </Button>
              </div>
              <p className="mt-4 text-center text-xs text-muted-foreground">
                Your name, city and the rest of your profile come later — you can add them whenever you like.
              </p>
            </motion.section>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
};

export default Onboarding;
